/**
 * Base HTTP client with error handling
 */

import type { ApiError } from './types'

/**
 * Custom error class for API errors
 */
export class ApiErrorException extends Error {
  constructor(
    public readonly error: string,
    public readonly code: string,
    public readonly status: number
  ) {
    super(error)
    this.name = 'ApiErrorException'
  }
}

/**
 * Base URL for API requests
 * Defaults to empty string to use the same origin (works with proxy in dev)
 */
export const BASE_URL = ''

/**
 * Get JWT token from localStorage
 */
function getAuthToken(): string | null {
  return localStorage.getItem('auth_token')
}

/**
 * Set JWT token in localStorage
 */
export function setAuthToken(token: string): void {
  localStorage.setItem('auth_token', token)
}

/**
 * Remove JWT token from localStorage
 */
export function removeAuthToken(): void {
  localStorage.removeItem('auth_token')
}

/**
 * Get API key from localStorage
 */
function getApiKey(): string | null {
  return localStorage.getItem('api_key')
}

/**
 * Set API key in localStorage
 */
export function setApiKey(apiKey: string): void {
  localStorage.setItem('api_key', apiKey)
}

/**
 * Remove API key from localStorage
 */
export function removeApiKey(): void {
  localStorage.removeItem('api_key')
}

/**
 * Generic request wrapper with error handling
 *
 * @param path - API endpoint path (e.g., '/api/ideas')
 * @param options - Fetch options
 * @returns Promise resolving to the response data
 * @throws ApiErrorException on API errors
 */
export async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${BASE_URL}${path}`

  // Get API key or auth token and add to headers if available
  const apiKey = getApiKey()
  const token = getAuthToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }

  // Skip auth headers for auth endpoints (login) to avoid circular dependency
  if (!path.startsWith('/api/auth/')) {
    // Prefer API key over JWT token if both are available
    if (apiKey) {
      headers['X-API-Key'] = apiKey
    } else if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers: headers as HeadersInit,
    })

    // Handle non-OK responses
    if (!response.ok) {
      // Handle 401 Unauthorized - redirect to login
      if (response.status === 401) {
        removeAuthToken()
        // Only redirect if we're not already on the login page
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login'
        }
      }

      // Try to parse error response
      let apiError: ApiError
      try {
        apiError = await response.json()
      } catch {
        // If JSON parsing fails, create a generic error
        apiError = {
          error: response.statusText || 'Request failed',
          code: `HTTP_${response.status}`,
        }
      }

      throw new ApiErrorException(
        apiError.error,
        apiError.code,
        response.status
      )
    }

    // Handle 204 No Content (e.g., DELETE requests)
    if (response.status === 204) {
      return undefined as T
    }

    // Parse and return JSON response
    return await response.json()
  } catch (error) {
    // Re-throw ApiErrorException as-is
    if (error instanceof ApiErrorException) {
      throw error
    }

    // Handle network errors or other fetch failures
    if (error instanceof Error) {
      throw new ApiErrorException(
        error.message,
        'NETWORK_ERROR',
        0
      )
    }

    // Fallback for unknown errors
    throw new ApiErrorException(
      'An unknown error occurred',
      'UNKNOWN_ERROR',
      0
    )
  }
}
